// App.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Link, Navigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "./index.css";
import { apiUrl } from "./config/api";

import ScanPage from "./pages/ScanPage";
import BarcodeDemo from "./pages/BarcodeDemo";
import StockInPage from "./pages/StockInPage";
import ProductsAddPage from "./pages/ProductsAddPage";
import UploadProductImage from "./pages/UploadProductImage";
import ProductsListPage from "./pages/ProductsListPage";
import OrdersPage from "./pages/OrdersPage";
import OrdersProfitPage from "./pages/OrdersProfitPage";
import QRCompletePage from "./pages/QRCompletePage";
import EditProductPage from "./pages/EditProductPage";
import AddGroupPage from "./pages/AddGroupPage";
import OrdersProfitSummaryPage from "./pages/OrdersProfitSummaryPage";
import ScanWholesalePage from "./pages/ScanWholesalePage";
import ScanCostCheckPage from "./pages/ScanCostCheckPage";
import ProductsStockAdminPage from "./pages/ProductsStockAdminPage";
import ProductsDeletePage from "./pages/ProductsDeletePage";
import OrdersDeletePage from "./pages/OrdersDeletePage";
import OrdersDateEditPage from "./pages/OrdersDateEditPage";
import AddKeychainOptionPage from "./pages/AddKeychainOptionPage";
import StockInKeychainPage from "./pages/StockInKeychainPage";
import InventoryMovementsListPage from "./pages/InventoryMovementsListPage";
import ScanMallPage from "./pages/ScanMallPage";
import ProductsZeroStockPage from "./pages/ProductsZeroStockPage";
import ExpensesPage from "./pages/ExpensesPage";
import LoginPage from "./pages/LoginPage";
import CreateUserPage from "./pages/CreateUserPage";
import UserListPage from "./pages/UserListPage";
import SaleChannelsPage from "./pages/SaleChannelsPage";
import TikTokShopOrdersPage from "./pages/TikTokShopOrdersPage";
import ScanSpecialItemsPage from "./pages/ScanSpecialItemsPage";
import StockTransferPage from "./pages/StockTransferPage";
import TiktokCartPage from "./pages/TiktokCartPage";
import USAStockPage from "./pages/USAStockPage";
import ScanToUSAPage from "./pages/ScanToUSAPage";
import USASalesPayoutPage from "./pages/USASalesPayoutPage";
import ScanUSASalesPage from "./pages/ScanUSASalesPage";
import USAExchangeRatePage from "./pages/USAExchangeRatePage";



// ------------------ Types ------------------

type NavCard = {
  id: string;
  title: string;
  link: string;
  desc?: string;
};

type AuthUser = {
  id: number;
  username: string;
  full_name: string;
  role: string;
  sale_channels: Array<{
    id: number;
    code: string;
    label: string;
    is_active: boolean;
  }>;
};

type AuthMeResponse = {
  ok?: boolean;
  user?: AuthUser;
  error?: string;
};

const TOKEN_STORAGE_KEY = "bg_shop_token";
const USER_STORAGE_KEY = "bg_shop_user";
const AUTH_ME_API = apiUrl("/auth/me");
const AUTH_DOCK_COLLAPSED_KEY = "bg_shop_auth_dock_collapsed";
const THEME_STORAGE_KEY = "bg_shop_theme";

function ProtectedRoute({
  isAuthenticated,
  children,
}: {
  isAuthenticated: boolean;
  children: JSX.Element;
}): JSX.Element {
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

function AuthStatusDock({
  currentUser,
  onLogout,
  theme,
  onToggleTheme,
}: {
  currentUser: AuthUser | null;
  onLogout: () => void;
  theme: "light" | "dark";
  onToggleTheme: () => void;
}): JSX.Element | null {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem(AUTH_DOCK_COLLAPSED_KEY) === "1";
  });

  useEffect(() => {
    localStorage.setItem(AUTH_DOCK_COLLAPSED_KEY, collapsed ? "1" : "0");
  }, [collapsed]);

  if (!currentUser) {
    return null;
  }

  return (
    <div className={`auth-status-dock ${collapsed ? "auth-status-dock-collapsed" : ""}`}>
      <div className="auth-status-dock-card">
        <button
          type="button"
          className="auth-status-dock-trigger"
          onClick={() => setCollapsed((prev) => !prev)}
          aria-label={collapsed ? "Expand login status" : "Collapse login status"}
          title={collapsed ? "Expand login status" : "Collapse login status"}
        >
          <span className="auth-status-avatar" aria-hidden="true">
            {currentUser.full_name?.trim()?.charAt(0)?.toUpperCase() || "U"}
          </span>

          <span className="auth-status-summary">
            <span className="auth-status-mini-label">บัญชี</span>
            <span className="auth-status-mini-user">{currentUser.full_name}</span>
          </span>

          <span className="auth-status-toggle-icon" aria-hidden="true">
            {collapsed ? "⌄" : "⌃"}
          </span>
        </button>

        <div className="auth-status-content">
          <div className="auth-status-content-meta">
            <div className="auth-status-name">{currentUser.full_name}</div>
            <div className="auth-status-role">{currentUser.role}</div>
          </div>
          <div className="d-flex flex-column gap-1 align-items-end">
            <button
              type="button"
              className="auth-status-theme-btn"
              onClick={onToggleTheme}
              title={theme === "dark" ? "เปลี่ยนเป็นโหมดสว่าง" : "เปลี่ยนเป็นโหมดมืด"}
            >
              {theme === "dark" ? "☀ สว่าง" : "🌙 มืด"}
            </button>
            <button
              type="button"
              className="auth-status-logout"
              onClick={onLogout}
            >
              Logout
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const INITIAL_CARDS: NavCard[] = [
  { id: "scan", title: "แสกนบันทึกสินค้าขาย", link: "/scan" },
  { id: "scanWholesale", title: "ScanWholesalePage", link: "/ScanWholesalePage" },
  { id: "scanMallPage", title: "ScanMallPage", link: "/ScanMallPage" },
  { id: "barcode", title: "Barcode demo", link: "/barcode" },
  { id: "stockIn", title: "StockInPage", link: "/StockInPage" },
  { id: "productsAdd", title: "ProductsAddPage", link: "/ProductsAddPage" },
  { id: "uploadImg", title: "UploadProductImage", link: "/UploadProductImage" },
  { id: "productsList", title: "ProductsListPage", link: "/ProductsListPage" },
  { id: "orders", title: "OrdersPage", link: "/OrdersPage" },
  { id: "ordersProfit", title: "OrdersProfitPage", link: "/OrdersProfitPage" },
  { id: "qrComplete", title: "QRCompletePage", link: "/QRCompletePage" },
  { id: "editProduct", title: "EditProductPage", link: "/EditProductPage" },
  { id: "addGroup", title: "AddGroupPage", link: "/AddGroupPage" },
  {
    id: "ordersProfitSummary",
    title: "OrdersProfitSummaryPage",
    link: "/OrdersProfitSummaryPage",
  },
  { id: "scanCostCheck", title: "ScanCostCheckPage", link: "/ScanCostCheckPage" },
  { id: "productsStockAdminPage", title: "ProductsStockAdminPage", link: "/ProductsStockAdminPage" },
  { id: "productsDeletePage", title: "ProductsDeletePage", link: "/ProductsDeletePage" },
  { id: "ordersDeletePage", title: "OrdersDeletePage", link: "/OrdersDeletePage" },
  {
    id: "ordersDateEditPage",
    title: "OrdersDateEditPage",
    link: "/OrdersDateEditPage",
    desc: "แก้วันที่ย้อนหลังของบิล",
  },
  { id: "addKeychainOptionPage", title: "AddKeychainOptionPage", link: "/AddKeychainOptionPage" },
  { id: "stockInKeychainPage", title: "StockInKeychainPage", link: "/StockInKeychainPage" },
  { id: "inventoryMovementsListPage", title: "InventoryMovementsListPage", link: "/InventoryMovementsListPage" },
  { id: "productsZeroStockPage", title: "ProductsZeroStockPage", link: "/ProductsZeroStockPage" },
  {
    id: "expensesPage",
    title: "บันทึกรายจ่าย",
    link: "/ExpensesPage",
    desc: "เพิ่ม แก้ไข ลบ และกรองค่าใช้จ่าย",
  },


  {
    id: "createUserPage",
    title: "CreateUserPage",
    link: "/CreateUserPage",
    desc: "Create new user accounts and assign roles",
  },
  {
    id: "userListPage",
    title: "UserListPage",
    link: "/UserListPage",
    desc: "View user accounts and assigned roles",
  },
  {
    id: "saleChannelsPage",
    title: "SaleChannelsPage",
    link: "/SaleChannelsPage",
    desc: "Create, edit, and manage sale locations",
  },
  {
    id: "tiktokShopOrdersPage",
    title: "TikTokShopOrdersPage",
    link: "/TikTokShopOrdersPage",
    desc: "Manage TikTok Shop orders and update fees later",
  },
  {
    id: "scanSpecialItemsPage",
    title: "ScanSpecialItemsPage",
    link: "/ScanSpecialItemsPage",
    desc: "Manage pot and flower quick-add items for the scan page",
  },
  {
    id: "stockTransferPage",
    title: "StockTransferPage",
    link: "/StockTransferPage",
    desc: "ย้าย stock ระหว่าง MAIN, SHOP และ TIKTOK พร้อมดูประวัติ",
  },
  {
    id: "tiktokCartPage",
    title: "TikTok Cart",
    link: "/TiktokCartPage",
    desc: "จำลองตะกร้า TikTok เทียบกับ stock TIKTOK",
  },
  {
    id: "usaStockPage",
    title: "USAStockPage",
    link: "/USAStockPage",
    desc: "รายการสินค้าที่มี stock ใน USA",
  },
  {
    id: "scanToUSAPage",
    title: "🇺🇸 สแกนย้ายของไป USA",
    link: "/ScanToUSAPage",
    desc: "สแกน batch + ย้าย stock ไป USA พร้อมต้นทุน",
  },
  {
    id: "usaSalesPayoutPage",
    title: "🇺🇸 บันทึกยอดขาย USA",
    link: "/USASalesPayoutPage",
    desc: "กรอกยอดขาย USD/THB ของบิล USA-...",
  },
  {
    id: "scanUSASalesPage",
    title: "🇺🇸 สแกนบันทึกขาย USA",
    link: "/ScanUSASalesPage",
    desc: "สแกนขายแบบตะกร้า + บันทึก USD-only",
  },
  {
    id: "usaExchangeRatePage",
    title: "🇺🇸 ใส่เรทขาย USA",
    link: "/USAExchangeRatePage",
    desc: "คำนวณ THB ให้บิล USA ที่ยังไม่มีเรท",
  },
];

const TAB_CONFIG = [
  { id: 0, label: "เลย์เอาต์ 1" },
  { id: 1, label: "เลย์เอาต์ 2" },
  { id: 2, label: "เลย์เอาต์ 3" },
];

// ------------------ หน้าเมนูหลัก ------------------

function Home({
  currentUser,
  onLogout,
}: {
  currentUser: AuthUser | null;
  onLogout: () => void;
}): JSX.Element {
  const [cardsByTab, setCardsByTab] = useState<NavCard[][]>(() => [
    [...INITIAL_CARDS],
    [...INITIAL_CARDS],
    [...INITIAL_CARDS],
  ]);

  const [activeTab, setActiveTab] = useState<number>(0);
  const [isEditMode, setIsEditMode] = useState(false);

  const [searchText, setSearchText] = useState<string>("");

  const dragIndexRef = useRef<number | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [justMovedId, setJustMovedId] = useState<string | null>(null);

  const cards: NavCard[] = cardsByTab[activeTab] ?? [];

  const normalizedQuery = searchText.trim().toLowerCase();
  const isSearchMode = normalizedQuery.length > 0;

  const allCards = useMemo<NavCard[]>(() => {
    const map = new Map<string, NavCard>();
    cardsByTab.forEach((tabCards) => {
      tabCards.forEach((card) => {
        if (!map.has(card.id)) {
          map.set(card.id, card);
        }
      });
    });
    return Array.from(map.values());
  }, [cardsByTab]);

  const visibleCards: NavCard[] = useMemo(() => {
    if (!isSearchMode) {
      return cards;
    }

    return allCards.filter((card) => {
      const title = card.title.toLowerCase();
      const desc = (card.desc ?? "").toLowerCase();
      const link = card.link.toLowerCase();
      return (
        title.includes(normalizedQuery) ||
        desc.includes(normalizedQuery) ||
        link.includes(normalizedQuery)
      );
    });
  }, [isSearchMode, cards, allCards, normalizedQuery]);

  const handleDragStart = (index: number): void => {
    if (!isEditMode || isSearchMode) return;
    dragIndexRef.current = index;
    setDraggingId(cards[index].id);
  };

  const handleDragOver: React.DragEventHandler<HTMLDivElement> = (e) => {
    if (!isEditMode || isSearchMode) return;
    e.preventDefault();
  };

  const handleDrop = (index: number): void => {
    if (!isEditMode || isSearchMode) return;

    const from = dragIndexRef.current;
    if (from == null || from === index) {
      setDraggingId(null);
      dragIndexRef.current = null;
      return;
    }

    const currentCards = cardsByTab[activeTab] ?? [];
    const cardAtFrom = currentCards[from];
    if (!cardAtFrom) {
      setDraggingId(null);
      dragIndexRef.current = null;
      return;
    }
    const movedId = cardAtFrom.id;

    setCardsByTab((prev) => {
      const next: NavCard[][] = prev.map((arr) => [...arr]);
      const current: NavCard[] = next[activeTab];

      const cardToMove = current[from];
      if (!cardToMove) {
        return prev;
      }

      current.splice(from, 1);
      current.splice(index, 0, cardToMove);

      return next;
    });

    setJustMovedId(movedId);
    window.setTimeout(() => setJustMovedId(null), 800);

    setDraggingId(null);
    dragIndexRef.current = null;
  };

  return (
    // << เปลี่ยนตรงนี้: เอา d-flex / align-items-center / justify-content-center ออก >>
    <div className="min-vh-100 bg-primary">
      <div className="container py-4 py-md-5 text-light">
        {/* Header + ปุ่มโหมดแก้ไข + ช่องค้นหา */}
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-3 gap-2">
          <h4 className="mb-0">เมนูหลัก</h4>

          <div className="d-flex flex-column flex-md-row align-items-stretch gap-2">
            <form
              className="d-flex"
              role="search"
              onSubmit={(e) => {
                e.preventDefault();
              }}
            >
              <input
                type="search"
                className="form-control form-control-sm"
                placeholder="ค้นหาเมนู..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </form>

            <div className="d-flex gap-2 justify-content-end">
              <div className="btn-group" role="group" aria-label="เลือกเลย์เอาต์เมนู">
                {TAB_CONFIG.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    className={
                      "btn btn-sm " +
                      (activeTab === tab.id ? "btn-light text-primary" : "btn-outline-light")
                    }
                    onClick={() => {
                      setActiveTab(tab.id);
                    }}
                    disabled={isSearchMode}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              <button
                type="button"
                className={`btn btn-sm ${isEditMode ? "btn-warning" : "btn-outline-light"}`}
                onClick={() => setIsEditMode((v) => !v)}
                disabled={isSearchMode}
              >
                {isEditMode ? "✓ กำลังจัดตำแหน่ง" : "แก้ไขตำแหน่งเมนู"}
              </button>
            </div>
          </div>
        </div>

        {/* Grid การ์ดเมนู */}
        <div className="row row-cols-1 row-cols-sm-2 row-cols-lg-3 g-3 g-lg-4 justify-content-center">
          {visibleCards.map((card, i) => (
            <div key={card.id} className="col d-flex">
              <div
                className={
                  "w-100 rounded-3 " +
                  (isEditMode && !isSearchMode ? "border border-light border-2" : "")
                }
                draggable={isEditMode && !isSearchMode}
                onDragStart={() => handleDragStart(i)}
                onDragOver={handleDragOver}
                onDrop={() => handleDrop(i)}
              >
                <Link
                  to={card.link}
                  className={
                    "card w-100 shadow-sm border-0 rounded-3 text-decoration-none " +
                    (draggingId === card.id
                      ? "bg-warning-subtle"
                      : justMovedId === card.id
                      ? "bg-success-subtle"
                      : "bg-secondary")
                  }
                  onClick={(e) => {
                    if (isEditMode) {
                      e.preventDefault();
                    }
                  }}
                >
                  <div
                    className="d-flex align-items-center justify-content-center p-3"
                    style={{ height: "120px" }}
                  >
                    <div className="text-center d-flex flex-column align-items-center gap-1">
                      <h6 className="card-title text-black mb-1">{card.title}</h6>
                      <small className="text-white opacity-75">
                        {card.desc ?? "รายละเอียดสั้น ๆ"}
                      </small>
                    </div>
                  </div>
                </Link>
              </div>
            </div>
          ))}
        </div>

        {visibleCards.length === 0 && (
          <div className="mt-3 text-center small text-light">
            ไม่พบเมนูที่ตรงกับคำค้นหา
            {isSearchMode && searchText && (
              <>
                {" "}
                <span className="fw-semibold">"{searchText}"</span>
              </>
            )}
          </div>
        )}

        {isEditMode && !isSearchMode && (
          <div className="mt-3 small text-light-50">
            * โหมดแก้ไข (เลย์เอาต์ {activeTab + 1}): ลากการ์ดเพื่อจัดลำดับใหม่ — สีเหลือง =
            การ์ดที่กำลังลาก, สีเขียว = การ์ดที่เพิ่งย้าย
          </div>
        )}

        {isSearchMode && (
          <div className="mt-3 small text-light-50">
            * โหมดค้นหา: กำลังแสดงเฉพาะเมนูที่ตรงกับคำว่า "{searchText}" (ไม่สนใจเลย์เอาต์)
          </div>
        )}
      </div>
    </div>
  );
}

// ------------------ รวม Routes ------------------

export default function App(): JSX.Element {
  const [authLoading, setAuthLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState<AuthUser | null>(null);
  const isAuthenticated = currentUser != null;

  const [theme, setTheme] = useState<"light" | "dark">(() => {
    const saved = localStorage.getItem(THEME_STORAGE_KEY);
    return saved === "dark" ? "dark" : "light";
  });

  useEffect(() => {
    document.documentElement.setAttribute("data-bs-theme", theme);
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  }, [theme]);

  function toggleTheme(): void {
    setTheme((prev) => (prev === "dark" ? "light" : "dark"));
  }

  useEffect(() => {
    async function bootstrapAuth(): Promise<void> {
      const token = localStorage.getItem(TOKEN_STORAGE_KEY) ?? "";
      const rawUser = localStorage.getItem(USER_STORAGE_KEY) ?? "";

      if (!token) {
        setCurrentUser(null);
        setAuthLoading(false);
        return;
      }

      if (rawUser) {
        try {
          const parsed = JSON.parse(rawUser) as AuthUser;
          if (parsed && typeof parsed === "object") {
            setCurrentUser({
              ...parsed,
              sale_channels: Array.isArray(parsed.sale_channels) ? parsed.sale_channels : [],
            });
          }
        } catch {
          localStorage.removeItem(USER_STORAGE_KEY);
        }
      }

      try {
        const resp = await fetch(AUTH_ME_API, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (resp.status === 401) {
          localStorage.removeItem(TOKEN_STORAGE_KEY);
          localStorage.removeItem(USER_STORAGE_KEY);
          setCurrentUser(null);
          return;
        }

        const data = (await resp.json().catch(() => ({}))) as AuthMeResponse;
        if (resp.ok && data.ok && data.user) {
          setCurrentUser(data.user);
          localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(data.user));
        }
      } catch {
        // Only 401 means the token is bad; keep the cached session when the backend is down.
      } finally {
        setAuthLoading(false);
      }
    }

    void bootstrapAuth();
  }, []);

  useEffect(() => {
    function handleStorageChange(e: StorageEvent): void {
      if (e.key !== TOKEN_STORAGE_KEY && e.key !== USER_STORAGE_KEY) {
        return;
      }

      const token = localStorage.getItem(TOKEN_STORAGE_KEY) ?? "";
      const rawUser = localStorage.getItem(USER_STORAGE_KEY) ?? "";

      if (!token) {
        setCurrentUser(null);
        return;
      }

      if (!rawUser) {
        return;
      }

      try {
        const parsed = JSON.parse(rawUser) as AuthUser;
        if (parsed && typeof parsed === "object") {
          setCurrentUser({
            ...parsed,
            sale_channels: Array.isArray(parsed.sale_channels) ? parsed.sale_channels : [],
          });
        }
      } catch {
        setCurrentUser(null);
      }
    }

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  function handleLogout(): void {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(USER_STORAGE_KEY);
    setCurrentUser(null);
  }

  function handleLoginSuccess({
    token,
    user,
  }: {
    token: string;
    user: Omit<AuthUser, "sale_channels">;
  }): void {
    // PMN-CRITICAL(/login): /auth/login does not include sale_channels, so this fallback must be refreshed by /auth/me before ScanPage has full permissions.
    localStorage.setItem(TOKEN_STORAGE_KEY, token);
    const fallbackUser: AuthUser = {
      ...user,
      sale_channels: [],
    };
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(fallbackUser));
    setCurrentUser(fallbackUser);

    void (async () => {
      try {
        const resp = await fetch(AUTH_ME_API, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const data = (await resp.json().catch(() => ({}))) as AuthMeResponse;
        if (!resp.ok || !data.ok || !data.user) {
          return;
        }

        localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(data.user));
        setCurrentUser(data.user);
      } catch {
        // ปล่อย fallback user เดิมไว้ก่อน ถ้า me ยังไม่ตอบ
      }
    })();
  }

  if (authLoading) {
    return (
      <div className="min-vh-100 bg-primary d-flex align-items-center justify-content-center text-light">
        <div className="text-center">
          <div className="spinner-border text-light mb-3" role="status" />
          <div>กำลังโหลดข้อมูลผู้ใช้...</div>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <div className="app-shell">
        <AuthStatusDock currentUser={currentUser} onLogout={handleLogout} theme={theme} onToggleTheme={toggleTheme} />
        <Routes>
        <Route
          path="/"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <Home currentUser={currentUser} onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/scan"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <ScanPage allowedSaleChannels={currentUser?.sale_channels ?? []} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ScanWholesalePage"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <ScanWholesalePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ScanMallPage"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <ScanMallPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/StockInPage"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <StockInPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ProductsAddPage"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <ProductsAddPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/barcode"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <BarcodeDemo />
            </ProtectedRoute>
          }
        />
        <Route
          path="/UploadProductImage"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <UploadProductImage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ProductsListPage"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <ProductsListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/OrdersPage"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <OrdersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/OrdersProfitPage"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <OrdersProfitPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/QRCompletePage"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <QRCompletePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/EditProductPage"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <EditProductPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/AddGroupPage"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <AddGroupPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/OrdersProfitSummaryPage"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <OrdersProfitSummaryPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ScanCostCheckPage"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <ScanCostCheckPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ProductsStockAdminPage"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <ProductsStockAdminPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ProductsDeletePage"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <ProductsDeletePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/OrdersDeletePage"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <OrdersDeletePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/OrdersDateEditPage"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <OrdersDateEditPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/AddKeychainOptionPage"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <AddKeychainOptionPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/StockInKeychainPage"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <StockInKeychainPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/InventoryMovementsListPage"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <InventoryMovementsListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ProductsZeroStockPage"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <ProductsZeroStockPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ExpensesPage"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <ExpensesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/CreateUserPage"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <CreateUserPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/UserListPage"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <UserListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/SaleChannelsPage"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <SaleChannelsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/TikTokShopOrdersPage"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <TikTokShopOrdersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ScanSpecialItemsPage"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <ScanSpecialItemsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/StockTransferPage"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <StockTransferPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/TiktokCartPage"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <TiktokCartPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/USAStockPage"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <USAStockPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ScanToUSAPage"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <ScanToUSAPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/USASalesPayoutPage"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <USASalesPayoutPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ScanUSASalesPage"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <ScanUSASalesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/USAExchangeRatePage"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <USAExchangeRatePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/login"
          element={
            isAuthenticated ? (
              <Navigate to="/" replace />
            ) : (
              <LoginPage onLogin={handleLoginSuccess} />
            )
          }
        />

        </Routes>
      </div>
    </Router>
  );
}
